// Main API
export { analyzeSaju } from './engine';
export { analyzeCompatibility } from './compatibility/index';

// Types
export type {
  // Stems & Branches
  HeavenlyStem,
  HeavenlyStemIndex,
  EarthlyBranch,
  EarthlyBranchIndex,
  // Elements
  Eumyang,
  Ohaeng,
  ElementRelation,
  // Pillars
  PillarPosition,
  Pillar,
  FourPillars,
  // Ten Gods
  TenGod,
  TenGodInfo,
  TenGodMapping,
  // Relations
  HeavenlyRelationType,
  EarthlyRelationType,
  RelationType,
  RelationOptions,
  Relation,
  FortuneRelation,
  // Sinsal
  TwelveSinsalName,
  GeneralSinsalName,
  SinsalName,
  SinsalEntry,
  SinsalAnalysis,
  // Fortune
  FortuneDirection,
  MajorFortuneEntry,
  MajorFortune,
  YearlyFortune,
  MonthlyFortune,
  DailyFortune,
  // Strength
  StrengthLevel,
  JeonwangPattern,
  DayMasterStrength,
  YongSinMethod,
  YongSin,
  YongSinResult,
  // Gyeok-guk
  RegularFormatType,
  SpecialFormatType,
  JongFormatType,
  GukFormatType,
  GyeokGukType,
  GyeokGukCategory,
  FormatStatus,
  PagyeokReason,
  GyeokGukYongSin,
  GyeokGukThresholds,
  GyeokGukAnalysis,
  // Tongbyeon
  TenGodNature,
  TenGodCategory,
  TenGodCombinationName,
  TenGodCombination,
  MissingCategoryInfo,
  TongbyeonAnalysis,
  // Presets & Modes
  SajuMode,
  StrengthMode,
  YongshinPriority,
  SinsalEmphasis,
  YongsinEffect,
  GyeokgukEffect,
  // Input/Output
  BirthInput,
  AnalysisOptions,
  TimeCorrectionInfo,
  TwelveStage,
  TwelveStageInfo,
  SolarTermEntry,
  SajuAnalysis,
  // Compatibility
  RelationshipType,
  CompatibilityInput,
  CrossRelationType,
  CrossRelation,
  OhaengHarmony,
  YongSinCompatibility,
  GuiinMatch,
  NapeumCompat,
  ZodiacCompat,
  SpousePalace,
  FortuneFlowMatch,
  FortuneFlowEntry,
  YearlyFortuneFlowEntry,
  FortuneFlowCompat,
  StarCross,
  ChildrenFortune,
  JohooCompat,
  StrengthCross,
  Intensity,
  GyeokGukCompat,
  TwelveStageCross,
  CompatibilityMode,
  MarriagePotential,
  SpouseRole,
  SangsaengjiCross,
  FortuneTimingEntry,
  TraditionalChecklist,
  GradeInput,
  CompatibilityGrade,
  DimensionKey,
  DimensionScore,
  GradeResult,
  CompatibilityAnalysis,
} from './types/index';

// Individual module exports for advanced usage
export { calculateFourPillars } from './pillars/four-pillars';
export type { FourPillarsResult } from './pillars/four-pillars';
export { getYearPillar } from './pillars/year-pillar';
export { getMonthPillar } from './pillars/month-pillar';
export { getDayPillar, getDayPillarRaw } from './pillars/day-pillar';
export { getHourPillar } from './pillars/hour-pillar';

export { analyzeTenGods, getTenGod } from './analysis/ten-gods';
export { analyzeTwelveStages, getTwelveStage } from './analysis/twelve-stages';
export { calculateOhaengDistribution, calculateOhaengDistributionSimple } from './analysis/five-element-dist';
export { analyzeDayMasterStrength } from './analysis/day-master-strength';
export { adjustStrengthByGyeokGuk } from './analysis/strength-adjust';
export { determineYongSin, determineAllYongSin } from './analysis/yong-sin';
export { determineJohuYongSin } from './analysis/yong-sin-johu';
export { determineTonggwanYongSin } from './analysis/yong-sin-tonggwan';
export { determineByeongyakYongSin } from './analysis/yong-sin-byeongyak';
export { analyzeGyeokGuk } from './analysis/gyeok-guk';
export { analyzeTongbyeon } from './analysis/tongbyeon';

export { analyzeRelations, analyzeFortuneRelations } from './relations/index';
export { resolvePreset, mergeWithPreset } from './presets';
export { analyzeSinsal } from './sinsal/index';
export { getNapeumOhaeng, getNapeumName } from './sinsal/definitions';

export { calculateMajorFortune, getFortuneDirection, calculateFortuneStartAge } from './fortune/major-fortune';
export { getYearlyFortune, getYearlyFortuneRange } from './fortune/yearly-fortune';
export { getMonthlyFortune } from './fortune/monthly-fortune';
export { getDailyFortune, getDailyFortuneRange } from './fortune/daily-fortune';

export { getSolarTermsForYear, findSolarLongitudeTime } from './astronomy/solar-terms';
export { solarLongitude, equationOfTime } from './astronomy/solar-longitude';
export { gregorianToJD, dateToJD, jdToGregorian, jdToDate } from './astronomy/julian-day';

export { lunarToSolar, solarToLunar } from './calendar/lunar-calendar';
export { applyTrueSolarTimeCorrection } from './calendar/date-utils';

// Core utilities
export { HEAVENLY_STEMS, EARTHLY_BRANCHES, SEXAGENARY_CYCLE } from './core/constants';
export { REGIONS, type Region } from './core/regions';
export { makePillar, makePillarFromStemBranch } from './core/sexagenary';
export { getStemElement, getBranchElement } from './core/five-elements';
export { getStemEumyang, getBranchEumyang } from './core/eumyang';
export { getHiddenStems, getMainHiddenStem } from './core/hidden-stems';
